/**
 * useConsentStatus — single TanStack Query source for /api/v1/consent/status.
 *
 * Shared between ConsentGate and ComplianceSetupGate so the endpoint is hit
 * once per session/org. Its loading state also gates the boot splash in
 * __root.tsx, so the user never sees a blank frame between splash and UI.
 *
 * Fail-open: on network/auth errors the query resolves with "all granted" so
 * the app keeps rendering — consent enforcement still runs server-side.
 */
import { useQuery } from '@tanstack/react-query';
import {
  consentStatusResponseSchema,
  type ConsentStatusResponse,
} from '@aica/api-types/schemas';
import { useClerkGate } from '@/hooks/useClerkGate';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useRole } from '@/contexts/RoleContext';
import { logError } from '@/lib/logger';

/**
 * Форма ответа берётся из контракта, а не выписывается здесь второй раз.
 *
 * Локальная копия отставала: `recording` в ней стоял необязательным, хотя
 * маршрут отдаёт его всегда, — и наоборот, экраны продолжали бы
 * компилироваться с типами, которых в ответе уже нет.
 */
export type ConsentStatusData = ConsentStatusResponse;

// Fail-open во всех полях: гейты должны разблокироваться, а не «на всякий
// случай» показать экран согласия человеку, который его уже дал. Настоящий
// запрет живёт на сервере — здесь фабрикуется только то, чем интерфейс
// решает, рисовать ли модалку.
const FAIL_OPEN: ConsentStatusData = {
  recording: true,
  terms_of_service: true,
  privacy_policy: true,
  complianceConfirmed: true,
  // Пусто, а не «как было»: это поле перечисляет действующие исторические
  // согласия, и придумать их при недоступном сервере значило бы показать
  // клиенту кнопку «Отозвать» для того, чего, может быть, нет.
  legacyGranted: [],
  // `false`, а не `true`: сервер недоступен — это не «настройки испорчены».
  // Обратное подняло бы предупреждение о нечитаемых настройках на каждом
  // сетевом сбое, то есть сообщило бы о поломке данных там, где её нет.
  complianceSettingsUnreadable: false,
  recordingWithdrawn: false,
};

export const consentStatusQueryKey = (orgId: string | null) =>
  ['consent', 'status', orgId] as const;

export function useConsentStatus() {
  const { authenticatedFetch } = useAuthFetch();
  const { isSignedIn, isLoaded: isClerkLoaded } = useClerkGate();
  const { selectedOrgId, globalRole, isLoading: isRoleLoading } = useRole();

  // RBAC allows consent.read only for client/admin. Other roles (agent,
  // supervisor) would always get a 403 — resolve fail-open without a network
  // call so the gates unblock and Sentry isn't spammed with expected errors.
  const canReadConsent = globalRole === 'client' || globalRole === 'admin';

  return useQuery({
    queryKey: consentStatusQueryKey(selectedOrgId),
    queryFn: async (): Promise<ConsentStatusData> => {
      if (!canReadConsent) {
        return FAIL_OPEN;
      }
      try {
        // Разбор, а не приведение типом: ответ, разъехавшийся с контрактом,
        // должен попасть в тот же fail-open, что и сетевая ошибка, — иначе
        // отсутствующее поле читается как `undefined` и гейт показывает
        // модалку тому, кто согласие уже дал.
        return consentStatusResponseSchema.parse(
          await authenticatedFetch<unknown>('/api/v1/consent/status')
        );
      } catch (err) {
        logError('consent.fetch_status_failed', {
          component: 'useConsentStatus',
          error: err instanceof Error ? err.message : 'Unknown',
        });
        return FAIL_OPEN;
      }
    },
    enabled: isClerkLoaded && !!isSignedIn && !!selectedOrgId && !isRoleLoading,
    staleTime: 5 * 60 * 1000,
  });
}
